/**
 * Application entry point — bootstraps the React tree with the auth layer
 * (task 3.12; Req 1.1, 1.2, 1.3, 2.6, 5.1, 5.2).
 *
 * The wrapping order is:
 *   1. ConfigError guard (no provider needed) — reads env, renders config-error
 *      screen and withholds all data when `readMsalConfig` returns `ok: false`.
 *   2. EntraAuthProvider — initializes MSAL with the resolved config.
 *   3. RequireAuth — gates children behind a valid access token, showing
 *      SignInScreen as fallback during the interactive sign-in phase.
 *   4. RoleGate — checks `ViaMind.Viewer` role, shows AccessDeniedScreen when
 *      the role is absent.
 *   5. AuthBridge — wires the auth adapters into the context providers that
 *      App.tsx reads (AuthedFetchProvider + SignOutProvider).
 *   6. BrowserRouter + App — the actual dashboard.
 *
 * Tests render `<App />` directly (inside a MemoryRouter) and do NOT go through
 * main.tsx, so the auth wrapper does not break the test suite. The auth layer
 * only activates in the real entry point when env vars are configured.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import App from "./App.tsx";
import { readMsalConfig } from "./auth/config.ts";
import { toEntraAuthConfig, EntraAuthProvider, RequireAuth, useSignOut } from "./auth/adapters.ts";
import { useAuthedFetch } from "./auth/useAuthedFetch.ts";
import { AuthedFetchProvider } from "./auth/AuthedFetchContext.tsx";
import { SignOutProvider } from "./auth/SignOutContext.tsx";
import SignInScreen from "./components/SignInScreen.tsx";
import AccessDeniedScreen from "./components/AccessDeniedScreen.tsx";
import ConfigErrorScreen from "./components/ConfigErrorScreen.tsx";
import RoleGate from "./components/RoleGate.tsx";

import "./styles/theme.css";
import "./styles/components.css";

/**
 * Bridge component that sits inside the auth tree (after RequireAuth + RoleGate)
 * and provides the authedFetch and signOut to the App via context. This exists
 * so that App.tsx and its children never import from `@viamericas/auth-react`
 * directly — keeping the test suite decoupled from the restricted package.
 */
function AuthBridge({ children }: { children: React.ReactNode }) {
  const authedFetch = useAuthedFetch();
  const signOut = useSignOut();
  const handleSignOut = () => void signOut();

  return (
    <AuthedFetchProvider value={authedFetch}>
      <SignOutProvider value={handleSignOut}>
        {children}
      </SignOutProvider>
    </AuthedFetchProvider>
  );
}

const rootElement = document.getElementById("root");
if (rootElement) {
  const configResult = readMsalConfig(import.meta.env, window.location.origin);

  createRoot(rootElement).render(
    <StrictMode>
      {configResult.ok ? (
        <EntraAuthProvider config={toEntraAuthConfig(configResult.config)}>
          <RequireAuth fallback={<SignInScreen />}>
            <RoleGate fallback={<AccessDeniedScreen />}>
              <AuthBridge>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </AuthBridge>
            </RoleGate>
          </RequireAuth>
        </EntraAuthProvider>
      ) : (
        <ConfigErrorScreen missing={configResult.missing} />
      )}
    </StrictMode>,
  );
}
